import { m } from "@plannello/i18n/messages";
import { getLocale } from "@plannello/i18n/runtime";
import type { ItemLite } from "@plannello/shared";
import { Button as AriaButton } from "react-aria-components";
import type { Catalog } from "@/data/catalog";
import { ItemLine } from "../ItemLine";
import { formatSpan, itemSpan } from "./model";

/**
 * Phones get the timeline as an agenda: dated items grouped by the month they start in, each with
 * its span as text. Dragging bars on a 375px screen helps nobody; dates are set in the item.
 */
export function TimelineAgenda({
  items,
  unscheduled,
  catalog,
  onOpen,
}: {
  items: ItemLite[];
  unscheduled: number;
  catalog: Catalog;
  onOpen: (item: ItemLite) => void;
}) {
  const dated = items
    .map((item) => ({ item, span: itemSpan(item) }))
    .filter((x): x is { item: ItemLite; span: NonNullable<ReturnType<typeof itemSpan>> } => !!x.span)
    .sort((a, b) => a.span.start - b.span.start);
  const monthFmt = new Intl.DateTimeFormat(getLocale(), { month: "long", year: "numeric", timeZone: "UTC" });
  const groups = new Map<string, typeof dated>();
  for (const d of dated) {
    const label = monthFmt.format(new Date(d.span.start * 86_400_000));
    const list = groups.get(label);
    if (list) list.push(d);
    else groups.set(label, [d]);
  }
  return (
    <div className="scroll-thin @container min-h-0 flex-1 overflow-y-auto px-5 pb-16">
      {dated.length === 0 ? <p className="pt-4 text-sm text-fg-muted">{m.tl_empty_dates()}</p> : null}
      {[...groups].map(([label, list]) => (
        <section key={label} aria-label={label} className="pt-4">
          <h2 className="pb-1.5 text-sm font-semibold text-fg">{label}</h2>
          <ul className="overflow-hidden rounded-panel border border-line">
            {list.map(({ item, span }) => (
              <li key={item.id} className="border-b border-line last:border-b-0">
                <AriaButton
                  onPress={() => onOpen(item)}
                  className="flex w-full flex-col items-start gap-1 px-3 py-2 text-left outline-hidden data-pressed:bg-active data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:-outline-offset-2 data-focus-visible:outline-focus"
                >
                  <span className="text-xs text-fg-muted tnum">{formatSpan(span.start, span.end, m.tl_to())}</span>
                  <ItemLine item={item} catalog={catalog} compact className="w-full" />
                </AriaButton>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {unscheduled > 0 ? (
        <p className="pt-4 text-xs text-fg-muted">
          {unscheduled === 1 ? m.tl_agenda_unscheduled_one() : m.tl_agenda_unscheduled({ count: unscheduled })}
        </p>
      ) : null}
    </div>
  );
}
